import React, { useState, useCallback, useMemo } from 'react';
import { Button, Modal, Badge, Row, Col, Card, Spinner, ProgressBar } from 'react-bootstrap';
import { FiDownload, FiUsers, FiX, FiDollarSign } from 'react-icons/fi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';

// Move helper functions outside component
const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

const formatDueDate = (paymentDate) => {
    if (!paymentDate) return "—";
    const date = moment(paymentDate).tz("Asia/Singapore");
    const day = date.date();
    const ordinalSuffix = getOrdinalSuffix(day);
    return `${day}${ordinalSuffix} ${date.format("MMM")}`;
};

const getStatusColor = (status) => {
    switch (status) {
        case "Paid": return "#16a34a";
        case "Pending": return "#d97706";
        case "Unpaid": return "#dc2626";
        default: return "#6b7280";
    }
};

const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
    ] : [0, 0, 0];
};

const calculateOverdueDays = (paymentDate, status) => {
    if (!paymentDate || status === "Paid") return "—";

    const dueDate = moment(paymentDate).tz("Asia/Singapore");
    const today = moment().tz("Asia/Singapore");

    if (today.isAfter(dueDate, 'day')) {
        return `${today.diff(dueDate, 'days')} days`;
    }
    return "—";
};

const BulkReportDownload = ({
    selectedRows,
    selectedStatusCounts,
    totalSelectedDeductionAmount,
    formatCurrency,
    getProductName,
    getBankAccountNumber,
    productCache,
    bankAccountCache,
    onClearSelection
}) => {
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(0);

    // Memoize calculateEMIDetails to prevent infinite re-renders
    const calculateEMIDetails = useCallback((row) => {
        const billAmount = parseFloat(row.bill_amount) || 0;
        const downPayment = parseFloat(row.down_pay) || 0;
        const emiPlan = parseInt(row.emi_plan_duration) || 1;
        const reports = row.reports || [];

        const paidReports = reports.filter(r => r.status === "Paid");
        const paidMonthsFromReports = paidReports.length;

        let monthlyEMI;
        let totalPaidAmount;
        let actualPaidMonths;

        if (!downPayment || downPayment === 0) {
            monthlyEMI = billAmount / emiPlan;
            totalPaidAmount = paidMonthsFromReports * monthlyEMI;
            actualPaidMonths = paidMonthsFromReports;
        } else {
            const remainingAmount = billAmount - downPayment;
            monthlyEMI = remainingAmount / (emiPlan - 1);
            const hasFirstMonthPaid = paidMonthsFromReports > 0;

            if (hasFirstMonthPaid) {
                totalPaidAmount = downPayment + ((paidMonthsFromReports - 1) * monthlyEMI);
                actualPaidMonths = paidMonthsFromReports;
            } else {
                totalPaidAmount = downPayment + (paidMonthsFromReports * monthlyEMI);
                actualPaidMonths = 1 + paidMonthsFromReports;
            }
        }

        const pendingAmount = billAmount - totalPaidAmount;
        const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
        const currentMonthReport = reports.find(r => r.month === currentMonth);

        return {
            monthlyEMI,
            paidAmount: totalPaidAmount,
            pendingAmount,
            paidMonths: actualPaidMonths,
            totalMonths: emiPlan,
            currentMonthStatus: currentMonthReport?.status,
            progressPercentage: emiPlan > 0 ? (actualPaidMonths / emiPlan) * 100 : 0,
            remainingMonths: emiPlan - actualPaidMonths,
            hasDownPayment: downPayment > 0,
            downPaymentAmount: downPayment
        };
    }, []);

    // Memoize class instance creation
    const BulkPDFGenerator = useMemo(() => {
        class BulkPDFGenerator {
            constructor(selectedRows, selectedStatusCounts, totalSelectedDeductionAmount, formatCurrency, getProductName, getBankAccountNumber) {
                this.doc = new jsPDF();
                this.selectedRows = selectedRows;
                this.selectedStatusCounts = selectedStatusCounts;
                this.totalSelectedDeductionAmount = totalSelectedDeductionAmount;
                this.formatCurrency = formatCurrency;
                this.getProductName = getProductName;
                this.getBankAccountNumber = getBankAccountNumber;
                this.currentDate = moment().tz("Asia/Singapore").format('DD MMM YYYY [at] hh:mm A');

                this.colors = {
                    primary: [44, 62, 80],
                    secondary: [128, 128, 128],
                    success: [22, 163, 74],
                    warning: [217, 119, 6],
                    danger: [220, 38, 38],
                    light: [248, 250, 252]
                };

                this.margin = { left: 14, right: 14, top: 15, bottom: 30 };
                this.pageWidth = 210;
                this.pageHeight = 297;
                this.currentPage = 1;
                this.currentY = this.margin.top;
                this.totalPages = 0;
            }

            checkPageBreak(requiredHeight = 50) {
                const remainingSpace = this.pageHeight - this.currentY - this.margin.bottom;
                if (remainingSpace < requiredHeight) {
                    this.doc.addPage();
                    this.currentPage++;
                    this.currentY = this.margin.top;
                    return true;
                }
                return false;
            }

            setupHeader(title) {
                this.currentY = this.margin.top;

                this.doc.setFontSize(14);
                this.doc.setTextColor(0, 0, 0);
                this.doc.setFont(undefined, 'bold');
                this.doc.text(title, this.margin.left, this.currentY);

                this.doc.setFontSize(7);
                this.doc.setTextColor(100, 100, 100);
                this.doc.setFont(undefined, 'normal');
                this.doc.text(`Generated on ${this.currentDate}`, this.margin.left, this.currentY + 5);

                this.doc.setDrawColor(200, 200, 200);
                this.doc.line(this.margin.left, this.currentY + 10, this.pageWidth - this.margin.right, this.currentY + 10);

                this.currentY += 15;
                return this.currentY;
            }

            addSection(title) {
                this.checkPageBreak(15);

                this.doc.setFontSize(10);
                this.doc.setTextColor(...this.colors.primary);
                this.doc.setFont(undefined, 'bold');
                this.doc.text(title.toUpperCase(), this.margin.left, this.currentY);

                this.doc.setDrawColor(...this.colors.primary);
                this.doc.line(this.margin.left, this.currentY + 1, 40, this.currentY + 1);

                this.doc.setFont(undefined, 'normal');
                this.currentY += 8;
                return this.currentY;
            }

            createModernTable(data, options = {}) {
                const {
                    headers = [],
                    columnStyles = {},
                    headStyles = {},
                    theme = 'grid',
                    spaceAfter = 10
                } = options;

                const estimatedRowHeight = 7;
                const estimatedHeaderHeight = 8;
                const estimatedTableHeight = estimatedHeaderHeight + (data.length * estimatedRowHeight);

                this.checkPageBreak(estimatedTableHeight + 15);

                const tableOptions = {
                    startY: this.currentY,
                    head: headers,
                    body: data,
                    theme: theme,
                    styles: {
                        fontSize: 6,
                        cellPadding: 1.5,
                        lineColor: [200, 200, 200],
                        lineWidth: 0.25,
                        minCellHeight: 4
                    },
                    headStyles: {
                        fillColor: this.colors.primary,
                        textColor: 255,
                        fontSize: 10,
                        fontStyle: 'bold',
                        halign: 'center',
                        cellPadding: 2,
                        ...headStyles
                    },
                    bodyStyles: {
                        fontSize: 6,
                        textColor: [51, 51, 51],
                        cellPadding: 1.5,
                        lineColor: [220, 220, 220]
                    },
                    alternateRowStyles: {
                        fillColor: [248, 250, 252]
                    },
                    columnStyles: {
                        0: { fontStyle: 'bold' },
                        ...columnStyles
                    },
                    margin: this.margin,
                    tableWidth: 'wrap',
                    didDrawPage: (data) => {
                        if (data.pageNumber > this.currentPage - 1) {
                            this.currentPage = data.pageNumber + 1;
                            this.currentY = this.margin.top;
                        }
                    },
                    didDrawCell: (data) => {
                        if (data.section === 'body' && data.column.index === 9) {
                            const status = data.cell.raw;
                            if (status && status !== '—') {
                                const color = getStatusColor(status);
                                const rgb = hexToRgb(color);

                                this.doc.setFillColor(...rgb);
                                this.doc.rect(data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2, 'F');
                                this.doc.setTextColor(255, 255, 255);
                                this.doc.setFontSize(4);
                                this.doc.text(status, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 1, { align: 'center' });
                            }
                        }
                    }
                };

                autoTable(this.doc, tableOptions);
                this.currentY = this.doc.lastAutoTable.finalY + 10;

                return this.currentY;
            }

            addPageNumbers() {
                const actualTotalPages = this.doc.internal.getNumberOfPages();

                for (let i = 1; i <= actualTotalPages; i++) {
                    this.doc.setPage(i);
                    this.doc.setFontSize(7);
                    this.doc.setTextColor(...this.colors.secondary);
                    this.doc.text(
                        `Page ${i} of ${actualTotalPages}`,
                        this.pageWidth - this.margin.right,
                        this.pageHeight - 10,
                        { align: 'right' }
                    );
                }

                return actualTotalPages;
            }

            generateCoverPage() {
                this.setupHeader('BULK EMI REPORTS SUMMARY');
                this.addSection('Report Overview');

                this.doc.setFontSize(8);
                this.doc.setTextColor(0, 0, 0);

                const metrics = [
                    [`Total Customers:`, this.selectedRows.length],
                    [`Total Monthly EMI:`, this.formatCurrency(this.totalSelectedDeductionAmount)],
                    [`Average EMI per Customer:`, this.formatCurrency(this.totalSelectedDeductionAmount / this.selectedRows.length)]
                ];

                metrics.forEach(([label, value], index) => {
                    this.doc.text(label, this.margin.left, this.currentY);
                    this.doc.text(value.toString(), this.margin.left + 45, this.currentY);
                    this.currentY += 4;
                });

                this.currentY += 10;
                this.addSection('Status Breakdown');

                let statusYOffset = this.currentY;
                const statusEntries = Object.entries(this.selectedStatusCounts).filter(([_, count]) => count > 0);

                statusEntries.forEach(([status, count], index) => {
                    const column = index % 2;
                    const xPos = this.margin.left + (column * 80);

                    if (column === 0 && index > 0) {
                        statusYOffset += 4;
                    }

                    const color = getStatusColor(status);
                    this.doc.setFillColor(...hexToRgb(color));
                    this.doc.rect(xPos, statusYOffset - 2, 3, 3, 'F');
                    this.doc.setTextColor(44, 62, 80);
                    this.doc.setFontSize(7);
                    this.doc.text(`${status}: ${count}`, xPos + 5, statusYOffset);
                });

                this.currentY = statusYOffset + (statusEntries.length > 0 ? 10 : 6);
            }

            generateExecutiveSummary() {
                this.addSection('Executive Summary');

                const totalBillAmount = this.selectedRows.reduce((sum, row) => sum + (parseFloat(row.bill_amount) || 0), 0);
                const totalPaidAmount = this.selectedRows.reduce((sum, row) => {
                    const emiDetails = calculateEMIDetails(row);
                    return sum + emiDetails.paidAmount;
                }, 0);
                const totalPendingAmount = this.selectedRows.reduce((sum, row) => {
                    const emiDetails = calculateEMIDetails(row);
                    return sum + emiDetails.pendingAmount;
                }, 0);
                const totalMonthlyEMI = this.selectedRows.reduce((sum, row) => {
                    const emiDetails = calculateEMIDetails(row);
                    return sum + emiDetails.monthlyEMI;
                }, 0);

                const summaryData = [
                    ['Total Bill Amount:', this.formatCurrency(totalBillAmount)],
                    ['Total Paid Amount:', this.formatCurrency(totalPaidAmount)],
                    ['Total Pending Amount:', this.formatCurrency(totalPendingAmount)],
                    ['Total Monthly EMI:', this.formatCurrency(totalMonthlyEMI)],
                    ['Average Monthly EMI:', this.formatCurrency(totalMonthlyEMI / this.selectedRows.length)]
                ];

                this.doc.setFontSize(7);
                summaryData.forEach(([label, value], index) => {
                    this.doc.text(label, this.margin.left, this.currentY);
                    this.doc.text(value, this.margin.left + 45, this.currentY);
                    this.currentY += 3.5;
                });

                this.currentY += 10;
            }

            generateCustomerDetails(progressCallback) {
                const customersPerPage = 10;
                const totalPages = Math.ceil(this.selectedRows.length / customersPerPage);

                for (let page = 0; page < totalPages; page++) {
                    if (page > 0) {
                        this.doc.addPage();
                        this.currentPage = 2 + page;
                        this.setupHeader('CUSTOMER DETAILS');
                    } else {
                        this.addSection('Customer Details');
                    }

                    const startIdx = page * customersPerPage;
                    const endIdx = Math.min(startIdx + customersPerPage, this.selectedRows.length);
                    const currentBatch = this.selectedRows.slice(startIdx, endIdx);
                    const progress = ((page + 1) / totalPages) * 100;
                    progressCallback(progress);

                    const customerData = currentBatch.map((customer) => {
                        const emiDetails = calculateEMIDetails(customer);
                        const productName = this.getProductName(customer.product_id);
                        const overdueDays = calculateOverdueDays(customer.payment_date, emiDetails.currentMonthStatus);
                        const progressText = `${emiDetails.paidMonths}/${emiDetails.totalMonths}`;

                        const downPay = customer.down_pay;
                        const downPaymentDisplay = downPay && parseFloat(downPay) > 0
                            ? this.formatCurrency(parseFloat(downPay))
                            : "—";

                        return [
                            customer.full_name || '—',
                            productName.length > 12 ? productName.substring(0, 12) + '...' : productName,
                            this.formatCurrency(customer.bill_amount),
                            downPaymentDisplay,
                            this.formatCurrency(emiDetails.monthlyEMI),
                            this.formatCurrency(emiDetails.paidAmount),
                            this.formatCurrency(emiDetails.pendingAmount),
                            progressText,
                            emiDetails.currentMonthStatus || '—',
                            formatDueDate(customer.payment_date),
                            overdueDays
                        ];
                    });

                    this.createModernTable(customerData, {
                        headers: [
                            ['Customer Name', 'Product', 'Bill Amount', 'Down Payment', 'Monthly EMI', 'Paid', 'Pending', 'Progress', 'Status', 'Due Date', 'Overdue']
                        ],
                        columnStyles: {
                            0: { cellWidth: 20, fontStyle: 'bold' },
                            1: { cellWidth: 14 },
                            2: { cellWidth: 15, halign: 'right' },
                            3: { cellWidth: 15, halign: 'right' },
                            4: { cellWidth: 16, halign: 'right' },
                            5: { cellWidth: 15, halign: 'right' },
                            6: { cellWidth: 15, halign: 'right' },
                            7: { cellWidth: 12, halign: 'center' },
                            8: { cellWidth: 12, halign: 'center' },
                            9: { cellWidth: 14, halign: 'center' },
                            10: { cellWidth: 12, halign: 'center' }
                        },
                        headStyles: { fontSize: 6 },
                        bodyStyles: { fontSize: 6 },
                        spaceAfter: 5
                    });
                }
            }

            generate(progressCallback) {
                this.generateCoverPage();
                this.generateExecutiveSummary();
                this.generateCustomerDetails(progressCallback);

                this.totalPages = this.addPageNumbers();

                return this.doc;
            }
        }

        return BulkPDFGenerator;
    }, [calculateEMIDetails]);

    const generateBulkPDF = async () => {
        if (!selectedRows.length) return;

        setLoading(true);
        setGenerationProgress(0);

        try {
            const generator = new BulkPDFGenerator(
                selectedRows,
                selectedStatusCounts,
                totalSelectedDeductionAmount,
                formatCurrency,
                getProductName,
                getBankAccountNumber
            );

            const doc = generator.generate((progress) => {
                setGenerationProgress(progress);
            });

            const fileName = `Bulk_EMI_Reports_${moment().format('YYYY-MM-DD_HH-mm')}.pdf`;
            doc.save(fileName);

            toast.success(`Bulk report downloaded for ${selectedRows.length} customers`);
            setShowModal(false);
        } catch (error) {
            console.error('Bulk PDF generation error:', error);
            toast.error('Failed to generate bulk PDF report');
        } finally {
            setLoading(false);
            setGenerationProgress(0);
        }
    };

    const totalBillAmount = useMemo(() =>
        selectedRows.reduce((sum, row) => sum + (parseFloat(row.bill_amount) || 0), 0),
        [selectedRows]
    );

    const totalDownPayment = useMemo(() =>
        selectedRows.reduce((sum, row) => sum + (parseFloat(row.down_pay) || 0), 0),
        [selectedRows]
    );

    const avgEMI = useMemo(() =>
        selectedRows.length > 0 ? totalSelectedDeductionAmount / selectedRows.length : 0,
        [selectedRows.length, totalSelectedDeductionAmount]
    );

    return (
        <>
            <Button
                variant="success"
                size="sm"
                className="d-flex align-items-center gap-2"
                onClick={() => setShowModal(true)}
                disabled={selectedRows.length === 0}
                style={{ fontSize: '12px' }}
            >
                <FiDownload size={16} />
                Bulk Report ({selectedRows.length})
            </Button>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header className="position-relative border-0 pb-2">
                    <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                        <FiUsers />
                        Bulk Report Generation
                    </h6>
                    <Button
                        variant="link"
                        className="position-absolute top-0 end-0 p-1 m-1 text-muted"
                        onClick={() => setShowModal(false)}
                        style={{
                            fontSize: '14px',
                            width: '34px',
                            height: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onFocus={(e) => e.target.style.boxShadow = 'none'}
                    >
                        <FiX size={16} />
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <div className="space-y-4">
                        <Row>
                            <Col md={4}>
                                <Card className="border-0 bg-light text-center">
                                    <Card.Body className="py-3">
                                        <FiUsers size={20} className="text-primary mb-2" />
                                        <h5 className="fw-bold mb-1">{selectedRows.length}</h5>
                                        <small className="text-muted">Total Customers</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="border-0 bg-light text-center">
                                    <Card.Body className="py-3">
                                        <FiDollarSign size={20} className="text-success mb-2" />
                                        <h5 className="fw-bold mb-1">{formatCurrency(totalSelectedDeductionAmount)}</h5>
                                        <small className="text-muted">Monthly EMI</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="border-0 bg-light text-center">
                                    <Card.Body className="py-3">
                                        <FiDollarSign size={20} className="text-warning mb-2" />
                                        <h5 className="fw-bold mb-1">{formatCurrency(totalBillAmount)}</h5>
                                        <small className="text-muted">Total Bill Amount</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Card className="border-0 bg-light text-center">
                                    <Card.Body className="py-3">
                                        <FiDollarSign size={20} className="text-info mb-2" />
                                        <h5 className="fw-bold mb-1">{formatCurrency(totalDownPayment)}</h5>
                                        <small className="text-muted">Total Down Payment</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={6}>
                                <Card className="border-0 bg-light text-center">
                                    <Card.Body className="py-3">
                                        <FiDollarSign size={20} className="text-secondary mb-2" />
                                        <h5 className="fw-bold mb-1">{formatCurrency(avgEMI)}</h5>
                                        <small className="text-muted">Avg EMI per Customer</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Card className="border-0 bg-light">
                            <Card.Body>
                                <h6 className="fw-bold mb-3">Status Breakdown</h6>
                                <Row>
                                    {Object.entries(selectedStatusCounts).map(([status, count]) => (
                                        <Col key={status} md={4} className="mb-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <div
                                                    style={{
                                                        width: '10px',
                                                        height: '10px',
                                                        backgroundColor: getStatusColor(status),
                                                        borderRadius: '50%'
                                                    }}
                                                />
                                                <span className="fw-semibold small">{status}:</span>
                                                <Badge bg="secondary" className="ms-1">{count}</Badge>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>

                        {loading && (
                            <Card className="border-0 bg-light">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="fw-bold mb-0">Generating PDF...</h6>
                                        <span className="text-muted small">{Math.round(generationProgress)}%</span>
                                    </div>
                                    <ProgressBar
                                        now={generationProgress}
                                        animated
                                        style={{ height: '8px' }}
                                    />
                                    <small className="text-muted mt-2 d-block">
                                        Processing {selectedRows.length} customer records...
                                    </small>
                                </Card.Body>
                            </Card>
                        )}

                        <Card className="border-0 bg-light">
                            <Card.Body>
                                <h6 className="fw-bold mb-3">Report Contents:</h6>
                                <div className="row small">
                                    <div className="col-md-6">
                                        <ul className="list-unstyled">
                                            <li>✓ Cover page with summary</li>
                                            <li>✓ Executive financial overview</li>
                                            <li>✓ Customer details table</li>
                                            <li>✓ Down payment information</li>
                                        </ul>
                                    </div>
                                    <div className="col-md-6">
                                        <ul className="list-unstyled">
                                            <li>✓ Product information</li>
                                            <li>✓ Payment status tracking</li>
                                            <li>✓ Overdue days calculation</li>
                                            <li>✓ Monthly EMI calculation</li>
                                        </ul>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="outline-secondary" size='sm' style={{ fontSize: "12px" }} onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size='sm'
                        style={{ fontSize: "12px" }}
                        onClick={generateBulkPDF}
                        disabled={loading || selectedRows.length === 0}
                        className="d-flex align-items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <FiDownload size={16} />
                                Download Report
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BulkReportDownload;